import type {
  CustomWorkbookCartItemCreation,
  PaymentPayerType,
  PrintDocumentCartItemCreation,
  SignatureWorkbookCartItemCreation,
} from '@mathflat/domain/PodCart'
import { makeAutoObservable, runInAction } from 'mobx'

import { podMallController } from '@/controller/podMall.controller'
import WebviewService from '@/service/webview.service'
import appPostMsg from '@/utils/appPostMsg'
import { urlPreview } from '@/utils/download'
import { commonErrorMessage } from '@/utils/toast'

import type { CartItem } from '../_dto/CartItem'
import type { CartItemColl } from '../_dto/CartItemColl'
import type { CartItemStudent } from '../_dto/CartItemStudent'

class PODMallCartService {
  cartItemColl?: CartItemColl
  deliveryAmount = 3500

  constructor() {
    makeAutoObservable(this)
  }

  get hasCartItem() {
    return this.cartItemColl && this.cartItemColl.toArr.length > 0
  }

  get checkedItems() {
    if (!this.cartItemColl) return []

    return this.cartItemColl.toCheckedArr
  }

  get checkedItem() {
    if (!this.cartItemColl) return
    return this.cartItemColl.toArr.find((item) => this.cartItemColl?.isChecked(item.id))
  }

  get notiedCartItemCountStr() {
    if (this.cartItemColl?.toArr) {
      if (this.cartItemColl.toArr.length > 99) return '99+'
      if (this.cartItemColl.toArr.length === 0) return ''
      return `${this.cartItemColl.toArr.length}`
    }
  }

  async createCartItem(
    option:
      | SignatureWorkbookCartItemCreation
      | CustomWorkbookCartItemCreation
      | PrintDocumentCartItemCreation,
  ) {
    if ('thumbnailImageUrl' in option) {
      option.thumbnailImageUrl = option.thumbnailImageUrl.split('?')[0]
    }
    const cartItemId = await podMallController.postCartItem(option)
    return cartItemId
  }

  async updateCartItem(
    id: CartItem['id'],
    paymentPayerType: PaymentPayerType,
    option: Partial<
      Pick<
        CartItem,
        'pdfType' | 'printType' | 'additionalPrice' | 'productAmount' | 'discountAmount'
      > & {
        students: CartItemStudent[]
      }
    >,
  ) {
    if (!this.cartItemColl) return

    this.cartItemColl.updateOption(id, option)

    const targetItem = this.cartItemColl.get(id)

    if (!targetItem) return

    const { price, additionalPrice, productAmount, discountAmount } =
      await podMallController.putCartItem(id, targetItem.getUpdateParams(paymentPayerType))

    this.cartItemColl.updateOption(id, {
      price,
      additionalPrice,
      productAmount,
      discountAmount,
    })
  }

  async fetchSetCartItems(paymentType?: PaymentPayerType, opt?: { shouldCheckAll?: boolean }) {
    const { cartItemColl, deliveryAmount } = await podMallController.getCartItems({
      paymentPayerType: paymentType,
    })

    if (opt?.shouldCheckAll) {
      cartItemColl.allCheck()
    }

    runInAction(() => {
      this.cartItemColl = cartItemColl
      this.deliveryAmount = deliveryAmount
    })
    return cartItemColl
  }

  async deleteCartItem(ids: CartItem['id'][]) {
    if (!this.cartItemColl) return

    this.cartItemColl.deletes(ids)
    await podMallController.deleteCartItem(ids)
  }

  async getPreOrderIdByCheckedItems() {
    if (!this.cartItemColl) return

    return await podMallController.postPreOrder({
      cartItemIds: this.checkedItems.map((item) => item.id),
    })
  }

  async getPreOrderId(cartItemId: number) {
    return await podMallController.postPreOrder({ cartItemIds: [cartItemId] })
  }

  async getCartItemPreview(cartItemId: number) {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewCartItem(cartItemId.toString())
        return
      }
      const getPreviewUrl = async () => {
        const { presignedUrl, fileName } = await podMallController.getCartItemPreview(cartItemId)
        return { url: presignedUrl, fullFileName: fileName }
      }

      urlPreview(getPreviewUrl)
    } catch (e) {
      commonErrorMessage(e)
    }
  }
}

export const podMallCartService = new PODMallCartService()
